import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal, Input, BusinessHoursPicker } from '@components';
import { CallRoute, UpdateRouteData } from '../types';
import { ProcessedErrors } from '@utils/errorHandler';

interface EditRouteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: UpdateRouteData) => Promise<ProcessedErrors | undefined>;
  route: CallRoute | null;
}

export const EditRouteModal: React.FC<EditRouteModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  route,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<UpdateRouteData>({
    name: '',
    phone_number: '',
    business_hours: '',
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [backendErrors, setBackendErrors] = useState<ProcessedErrors | null>(null);

  useEffect(() => {
    if (route) {
      setFormData({
        name: route.name,
        phone_number: route.phone_number,
        business_hours: route.business_hours,
      });
    }
  }, [route]);

  useEffect(() => {
    if (!isOpen) {
      setBackendErrors(null);
    }
  }, [isOpen]);

  const handleInputChange = (field: keyof UpdateRouteData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }

    if (backendErrors?.fieldErrors[field]) {
      setBackendErrors(prev => {
        if (!prev) return null;
        const newFieldErrors = { ...prev.fieldErrors };
        delete newFieldErrors[field];
        return { ...prev, fieldErrors: newFieldErrors };
      });
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('callRouting.nameRequiredError');
    }

    if (!formData.phone_number.trim()) {
      newErrors.phone_number = t('callRouting.phoneNumberRequiredError');
    }

    if (!formData.business_hours.trim()) {
      newErrors.business_hours = t('callRouting.businessHoursRequiredError');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setBackendErrors(null);

    try {
      const backendErrs = await onSubmit(formData);

      if (backendErrs && Object.keys(backendErrs.fieldErrors).length > 0) {
        setBackendErrors(backendErrs);
        setLoading(false);
        return;
      }

      setBackendErrors(null);
      onClose();
    } catch (error) {
      // Errors are handled by parent component via onSubmit return value
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({ name: '', phone_number: '', business_hours: '' });
    setErrors({});
    setBackendErrors(null);
    onClose();
  };

  const getFieldError = (field: keyof UpdateRouteData): string | undefined => {
    return backendErrors?.fieldErrors[field] || errors[field];
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={t('callRouting.editRoute')} size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label
            htmlFor="route-name"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
          >
            {t('callRouting.nameRequired')}
          </label>
          <Input
            id="route-name"
            type="text"
            value={formData.name}
            onChange={e => handleInputChange('name', e.target.value)}
            placeholder={t('callRouting.enterRouteName')}
            errorText={getFieldError('name')}
            state={getFieldError('name') ? 'error' : 'default'}
            disabled={loading}
          />
        </div>

        <div>
          <label
            htmlFor="phone-number"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
          >
            {t('callRouting.phoneNumberRequired')}
          </label>
          <Input
            id="phone-number"
            type="tel"
            value={formData.phone_number}
            onChange={e => handleInputChange('phone_number', e.target.value)}
            placeholder={t('callRouting.phoneNumberPlaceholder')}
            errorText={getFieldError('phone_number')}
            state={getFieldError('phone_number') ? 'error' : 'default'}
            disabled={loading}
          />
        </div>

        <div>
          <label
            htmlFor="business-hours"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
          >
            {t('callRouting.businessHoursRequired')}
          </label>
          <BusinessHoursPicker
            value={formData.business_hours}
            onChange={value => handleInputChange('business_hours', value)}
            error={getFieldError('business_hours')}
            disabled={loading}
          />
        </div>

        <div className="flex justify-end space-x-3 pt-4">
          <Button type="button" variant="outline" onClick={handleClose} disabled={loading}>
            {t('callRouting.cancel')}
          </Button>
          <Button type="submit" variant="gradient-multi" disabled={loading}>
            {loading ? t('callRouting.updating') : t('callRouting.updateRoute')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
